import axios from "axios";
import firebase from "./firebase";
import request from "../infra/request";
import { STATUS } from "../constants";

// The path of an endpoint for one workflow (workflow-multi-api src/router/workflows). An id
// that is not text throws, as the Firestore call it replaced did, rather than reaching the
// backend as "undefined".
const workflowUrl = (workflowId, endpoint) => {
  if (typeof workflowId !== "string") {
    throw new Error("A workflow id is required.");
  }
  return `/admin/workflows/${encodeURIComponent(workflowId)}/${endpoint}`;
};
export const searchPatientsService = async ({ searchText }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.get(`${process.env.REACT_APP_CLIENTS}?search=${searchText}`, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};

export const fetchPatientsService = async ({ pages }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.get(`${process.env.REACT_APP_PATIENTS}?pages=${pages}`, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};
export const updateWorkflowNeedAttentionService = async (id, needAttention) => {
  try {
    await request.put(workflowUrl(id, "need-attention"), { needAttention });
    return { status: STATUS.SUCCEED };
  } catch (e) {
    return { status: STATUS.FAILED };
  }
};
export const fetchPatientDetailsService = async ({ clientId }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.get(`${process.env.REACT_APP_PATIENT}?clientId=${clientId}`, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};

export const trackPatientService = async ({ clientId, referralId }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.get(`${process.env.REACT_APP_TRACK_PATIENT}`, {
    params: { clientId, ...(referralId ? { referralId } : {}) },
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};

export const getReferredSpecialistsService = async ({ clientId }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.get(`${process.env.REACT_APP_API_HOST}/pms/admin/workflow/referred-specialists`, {
    params: { clientId },
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};

// Every workflow of the patient, whole, each with its document id as workflowId.
export const checkPatientService = async ({ clientId }) => {
  const response = await request.get("/admin/workflows", { params: { clientId } });
  return response.data.workflows;
};

export const createPatientService = async (client) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.post(
    `${process.env.REACT_APP_CREATE_PATIENT}`,
    { client },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

export const updatePatientService = async ({ id, clientId, client }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.patch(
    `${process.env.REACT_APP_CREATE_PATIENT}`,
    { id, clientId, client },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

/**
 * A blank category selection means "leave it alone", not "clear it" — mirroring
 * selectCategory, which ignores falsey values when writing Firestore. Without
 * this the two stores would drift: Firestore would keep the old category while
 * PMS took the mapper's default. The adapter also rejects "" outright, since
 * its category field is @IsNotEmpty.
 */
const normaliseCategory = (category) => category || undefined;

/**
 * Patches an existing PMS referral with the referral date and the WF category.
 *
 * No expectedAppointmentDate: that field is synced by the tasks endpoint, which
 * owns the Firestore write for it as well. Accepting it here would let a caller
 * move PMS while appointment_t.expected_appointment_date went stale.
 *
 * An omitted field leaves the stored value untouched.
 *
 * Renamed from updateReferralDateService (no longer date-only). The endpoint
 * path is unchanged so the API can deploy ahead of the UI.
 */
export const updateReferralDetailsService = async ({ id, referralId, referralDate, category }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  const body = { id, referralId };
  if (referralDate !== undefined) body.referralDate = referralDate;
  const normalisedCategory = normaliseCategory(category);
  if (normalisedCategory !== undefined) body.category = normalisedCategory;

  return axios.patch(`${process.env.REACT_APP_UPDATE_REFERRAL_DATE}`, body, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};

export const updateReferralService = async ({
  id,
  referralId,
  referralDate,
  clinic,
  referrer,
  category,
  expectedAppointmentDate,
}) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  const body = { id, referralId, referralDate, clinic, referrer };
  const normalisedCategory = normaliseCategory(category);
  if (normalisedCategory !== undefined) body.category = normalisedCategory;
  if (expectedAppointmentDate !== undefined) body.expectedAppointmentDate = expectedAppointmentDate;

  return axios.patch(`${process.env.REACT_APP_UPDATE_REFERRAL}`, body, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};

export const createAppointmentService = async ({ id, appointment, skipInitialSMS }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.post(
    `${process.env.REACT_APP_CREATE_APPOINTMENT}`,
    {
      id,
      appointment,
      selectedSMSTemplateSetId: appointment.selectedSMSTemplateSetId,
      skipInitialSMS,
    },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

export const sendSMSService = async (data) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.post(`${process.env.REACT_APP_SEND_SMS}`, data, { headers: { Authorization: `Bearer ${userIdToken}` } });
};

export const sendEmailService = async (data) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.post(`${process.env.REACT_APP_SEND_EMAIL}`, data, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};

export const createAppointmentNotesService = async ({ id, notes }) => {
  try {
    await request.put(workflowUrl(id, "appointment-notes"), { notes });
  } catch (err) {}
};

export const informAppointmentDeletion = async (data) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.post(`${process.env.REACT_APP_EMAIL_APPOINTMENT_DELETION}`, data, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};

export const deleteAppointmentService = async (requestBody) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.delete(`${process.env.REACT_APP_CREATE_APPOINTMENT}`, {
    headers: { Authorization: `Bearer ${userIdToken}` },
    data: requestBody,
  });
};

export const changeAppointmentService = async ({
  id,
  appointmentId,
  startDateTime,
  endDateTime,
  duration,
  skipInitialSMS,
  slotId,
  consultationType,
}) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.patch(
    `${process.env.REACT_APP_CREATE_APPOINTMENT}`,
    {
      id,
      appointmentId,
      startDateTime,
      endDateTime,
      duration,
      skipInitialSMS,
      ...(slotId ? { slotId } : {}),
      ...(consultationType ? { consultationType } : {}),
    },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

export const updateTasksService = async ({ id, tasks, workflowName = "WORKFLOW" }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.patch(
    `${process.env.REACT_APP_TASKS}`,
    { id, tasks, workflowName },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

export const trackAppointmentService = async ({ appointmentId, secretCode }) => {
  return axios.get(`${process.env.REACT_APP_TRACK_APPOINTMENT}`, {
    params: { id: appointmentId, secret: secretCode },
  });
};

export const getReferrerLocationsService = async ({ referrerId, clinicId }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  const referrerIdQuery = `referrerId=${referrerId}`;
  const query = clinicId ? `${referrerIdQuery}&clinicId=${clinicId}` : referrerIdQuery;
  return axios.get(`${process.env.REACT_APP_REFERRER_LOCATION}?${query}`, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};

export const updateTrackAppointmentService = async ({
  id,
  secret,
  nextSession,
  itemNumber,
  isLetterReady,
  testRequired,
  notes,
}) => {
  return axios.put(
    `${process.env.REACT_APP_TRACK_APPOINTMENT}`,
    {
      nextSession,
      itemNumber,
      isLetterReady,
      testRequired,
      notes,
    },
    { params: { id, secret } }
  );
};

export const archiveService = async ({ selectedCaIds }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.post(
    `${process.env.REACT_APP_ARCHIVE}`,
    { selectedCaIds },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

export const unArchiveService = async ({ selectedCaIds }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.post(
    `${process.env.REACT_APP_UN_ARCHIVE}`,
    { selectedCaIds },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

export const deleteWorkflowService = async ({ ids }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.post(
    `${process.env.REACT_APP_API_HOST}/admin/workflow/delete`,
    { ids },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

export const returnToGPService = async ({ id }) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.post(
    `${process.env.REACT_APP_API_HOST}/notification/return-to-gp`,
    { id },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

export const rebuildService = async (id) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.get(`${process.env.REACT_APP_REBUILD}?id=${id}`, {
    headers: { Authorization: `Bearer ${userIdToken}` },
  });
};

export const batchRebuildService = async (ids) => {
  const rebuildCalls = ids.map((id) => rebuildService(id));
  return Promise.all(rebuildCalls);
};

// Records the Acting Patient reply under sms.received, keyed by the backend's time in unix
// seconds.
export const updateSMSSent = async (activeCaId, result) => {
  await request.post(workflowUrl(activeCaId, "acting-patient-replies"), { result });
};

export const updateSmsStateService = async (activeCaId, answer) => {
  // Once acting patient is confirmed, we need to update the smsState in firebase client_appointments collection, which is used to track the sms state of the patient.
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.patch(
    `${process.env.REACT_APP_API_HOST}/pms/admin/client-appointments/${activeCaId}/sms-state`,
    { answer },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

export const selectHospital = async (activeCaId, location) => {
  if (location) {
    await request.put(workflowUrl(activeCaId, "location"), { location });
  }
};

export const selectCategory = async (activeCaId, category) => {
  if (category) {
    await request.put(workflowUrl(activeCaId, "category"), { category });
  }
};

export const updateFinNo = async (activeCaId, finNo) => {
  await request.put(workflowUrl(activeCaId, "fin-no"), { finNo });
};

export const updateSnapformSent = async (activeCaId, answer) => {
  const userIdToken = await firebase.currentUser().getIdToken();
  return axios.put(
    `${process.env.REACT_APP_API_HOST}/snapform/patient-submitted/${activeCaId}`,
    { answer },
    { headers: { Authorization: `Bearer ${userIdToken}` } }
  );
};

export const updateIvr = async (activeCaId, answer, { status }) => {
  const newIvr = { status };
  newIvr.reply = answer ? "1" : "2";
  console.log({ ivr: newIvr });
  // The backend works out the same reply from answer.
  await request.put(workflowUrl(activeCaId, "ivr-reply"), { answer, status });
};

export const incomingSmsService = async (from, body) => {
  const From = encodeURIComponent(from);
  const Body = encodeURIComponent(body);
  return axios.post(`${process.env.REACT_APP_INCOMING_SMS}`, `From=${From}&Body=${Body}`, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
  });
};

export const getHistoryWorkflowsForClient = async (firstName, lastName, dob) => {
  try {
    // A POST, so the patient's name and date of birth are not in the URL. The backend trims
    // them and matches the date of birth with and without "T00:00:00".
    const response = await request.post("/admin/workflows/history-search", {
      firstName,
      lastName,
      dateOfBirth: dob,
    });
    return { data: response.data.workflows };
  } catch (error) {
    return { error };
  }
};
